<script>
export default {
    name: 'ProviderTeamAccordion',

    props: {
        provider: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isOpen: false,
        };
    },

    computed: {
        team() {
            return this.$store.getters.PROVIDER_TEAM(this.provider.id);
        },
        members() {
            return this.team.items;
        },
        isLoading() {
            return this.team.loading;
        },
        hasError() {
            return this.team.error;
        },
    },

    methods: {
        async toggleAccordion() {
            this.isOpen = !this.isOpen;
            if (this.isOpen && this.members.length === 0 && !this.isLoading) {
                await this.loadTeam();
            }
        },

        async loadTeam() {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderTeamAccordion.loadTeam] provider:',
                    this.provider.id
                );
            }
            await this.$store.dispatch('loadProviderTeam', this.provider.id);
        },

        async handleRoleChange(member, event) {
            const role = event.target.value;
            if (role === member.role) return;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderTeamAccordion.handleRoleChange]',
                    { userId: member.user_id, role }
                );
            }
            try {
                await this.$store.dispatch('updateTeamMemberRole', {
                    providerId: this.provider.id,
                    userId: member.user_id,
                    role,
                });
            } catch {
                // Error popup handled in the store action; reset <select> visually
                // by forcing a re-render via the store getter (handled automatically)
            }
        },

        async handleRemoveMember(member) {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderTeamAccordion.handleRemoveMember]',
                    { userId: member.user_id }
                );
            }
            try {
                await this.$store.dispatch('removeTeamMember', {
                    providerId: this.provider.id,
                    userId: member.user_id,
                });
            } catch {
                // Error popup handled in the store action
            }
        },
    },
};
</script>

<template>
    <div class="team_accordion">
        <!-- Toggle button -->
        <button
            class="accordion_toggle"
            :class="{ accordion_open: isOpen }"
            @click="toggleAccordion"
            :title="isOpen ? 'Hide team' : 'Show team'"
        >
            <span class="toggle_arrow">{{ isOpen ? '▲' : '▼' }}</span>
            Team
            <span v-if="!isLoading && members.length > 0" class="member_count">
                ({{ members.length }})
            </span>
        </button>

        <!-- Accordion content -->
        <div v-if="isOpen" class="accordion_body">
            <!-- Loading -->
            <div v-if="isLoading" class="team_state">Loading team…</div>

            <!-- Error -->
            <div v-else-if="hasError" class="team_state team_error">
                Failed to load team.
                <button class="retry_btn" @click="loadTeam">Retry</button>
            </div>

            <!-- Empty -->
            <div v-else-if="members.length === 0" class="team_state">
                No active team members.
            </div>

            <!-- Team table -->
            <table v-else class="team_table">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="member in members"
                        :key="member.user_id"
                        class="team_row"
                    >
                        <td class="td_name">{{ member.user_name || '—' }}</td>
                        <td class="td_email">{{ member.user_email }}</td>
                        <td class="td_role">
                            <select
                                class="role_select"
                                :value="member.role"
                                @change="(e) => handleRoleChange(member, e)"
                            >
                                <option value="member">member</option>
                                <option value="owner">owner</option>
                            </select>
                        </td>
                        <td class="td_actions">
                            <button
                                class="btn_remove"
                                @click="handleRemoveMember(member)"
                                title="Remove team member"
                            >
                                Remove
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
.team_accordion {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
}

.accordion_toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    background: transparent;
    color: var(--text-color);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.accordion_toggle:hover,
.accordion_open {
    background: var(--background-secondary);
    border-color: var(--primary-color);
}

.toggle_arrow {
    font-size: 9px;
}

.member_count {
    font-weight: 400;
    color: var(--text-color-secondary);
}

.accordion_body {
    margin-top: 8px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-secondary);
    min-width: 480px;
    overflow: hidden;
}

.team_state {
    padding: 12px 16px;
    font-size: 13px;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
    gap: 8px;
}

.team_error {
    color: #ef4444;
}

.retry_btn {
    padding: 2px 8px;
    border: 1px solid #ef4444;
    border-radius: 4px;
    background: transparent;
    color: #ef4444;
    font-size: 11px;
    cursor: pointer;
}

.retry_btn:hover {
    background: #ef4444;
    color: #fff;
}

.team_table {
    width: 100%;
    border-collapse: collapse;
}

.team_table th {
    padding: 7px 12px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--text-color-secondary);
    border-bottom: 1px solid var(--border-color);
    background: var(--background-color);
}

.team_row {
    border-bottom: 1px solid var(--border-color);
}

.team_row:last-child {
    border-bottom: none;
}

.team_table td {
    padding: 8px 12px;
    font-size: 12px;
    vertical-align: middle;
    color: var(--text-color);
}

.td_email {
    font-family: monospace;
    font-size: 11px;
    color: var(--text-color-secondary);
}

.role_select {
    padding: 3px 6px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 12px;
    cursor: pointer;
}

.btn_remove {
    padding: 3px 10px;
    border: 1px solid #ef4444;
    border-radius: 4px;
    background: transparent;
    color: #ef4444;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn_remove:hover {
    background: #ef4444;
    color: #fff;
}
</style>
